import { assert } from '@ftr/contracts/shared/Assertions'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { generateUuid, Uuid } from '@ftr/contracts/type/shared'
import { SoftCreateUtteranceSttEdit } from '@ftr/regional-api-contracts'
import { Duration } from '@js-joda/core'
import { RealTimeSttSegmentMap } from '../../../types'
import { NoChangeReasonCode, OperationResult } from './operation-result'
import { bumpStartTimeEditsToProduceUniqueTime } from './unique-start-time-utils'
import { BoundaryError, checkEditForBoundaryErrors, getNextBoundaryPoint } from './within-boundaries-utils'

const DEFAULT_NEW_UTTERANCE_DURATION: Duration = Duration.ofSeconds(1)
export interface CreateRemarkAfterOperation {
  // e.g. enter pressed at end of remark
  type: 'createRemarkAfter'
  remarkId: Uuid
}

export function createRemarkAfterTransaction(
  operation: CreateRemarkAfterOperation,
  segmentMap: RealTimeSttSegmentMap,
): OperationResult {
  // Find the remark in the segment map
  const remark = segmentMap.findRemark(operation.remarkId)
  if (!remark) {
    throw new Error(`Remark with ID ${operation.remarkId} not found in segment map`)
  }

  const newRemarkStartTime = remark.endTime.plus(Duration.ofMillis(1))
  const uniqueStartTimeEdits = bumpStartTimeEditsToProduceUniqueTime(newRemarkStartTime, segmentMap, 'forward')
  const newUtteranceId = generateUuid()

  const nextBoundaryTime = getNextBoundaryPoint(newRemarkStartTime.toLocalTime(), segmentMap)?.atDate(
    newRemarkStartTime.toLocalDate(),
  )
  const intendedRemarkEndTime = newRemarkStartTime.plus(DEFAULT_NEW_UTTERANCE_DURATION)
  const newRemarkEndTime =
    nextBoundaryTime && nextBoundaryTime.isBefore(intendedRemarkEndTime) ? nextBoundaryTime : intendedRemarkEndTime

  const newUtteranceEdit: SoftCreateUtteranceSttEdit = {
    type: 'softCreateUtterance',
    utteranceId: newUtteranceId,
    dialect: remark.dialect ?? null,
    startTime: newRemarkStartTime,
    endTime: newRemarkEndTime,
    speakerId: remark.speakerId,
  }

  const boundaryError = checkEditForBoundaryErrors(newUtteranceEdit, segmentMap)
  if (boundaryError) {
    return {
      type: 'noChange',
      reasonCode: getSkippedReasonFromBoundaryError(boundaryError),
    }
  }

  const segmentForNewRemark = segmentMap.getSegmentAt(newUtteranceEdit.startTime)
  assert(segmentForNewRemark)

  return {
    type: 'transaction',
    transaction: {
      id: generateUuid(),
      edits: [...uniqueStartTimeEdits, newUtteranceEdit],
    },
    sideEffects: [
      {
        type: 'scrollToRemark',
        remarkId: newUtteranceId,
        segmentId: segmentForNewRemark.id,
      },
      {
        type: 'focusRemark',
        remarkId: newUtteranceId,
        selectionIndex: 0,
      },
    ],
  }
}

function getSkippedReasonFromBoundaryError(error: BoundaryError): NoChangeReasonCode {
  switch (error) {
    case 'start-end-not-in-session':
    case 'start-not-in-session':
    case 'end-not-in-session':
      return 'create-after-out-of-session'
    case 'start-end-in-inaccessible-sealed':
    case 'start-in-inaccessible-sealed':
    case 'end-in-inaccessible-sealed':
      return 'create-after-in-inaccessible-sealed'
    case 'start-end-cross-session-boundary':
    case 'start-end-cross-hearing-boundary':
    case 'start-end-cross-sealed-boundary':
      throw new Error('This operation should never produce a cross-boundary error.')
    default:
      assertUnreachable(error)
  }
}
